import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {IssueTypePicker} from '@github-ui/item-picker-issue-type-tsq'
import type {IssueItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {IssueTracksIcon} from '@primer/octicons-react'
import {useCallback} from 'react'

import {RepoIssuesBulkPickerAnchor, type RepoIssuesBulkActionProps} from './RepoIssuesBulkActionShared'

const NO_TYPE_ITEM = {
  id: SPECIAL_VALUES.noTypeData.id,
  description: '',
  descriptionVariant: 'inline',
  children: SPECIAL_VALUES.noTypeData.name,
  source: SPECIAL_VALUES.noTypeData as IssueTypePickerIssueType,
  groupId: '',
} satisfies ExtendedItemProps<IssueTypePickerIssueType>

function issueTypeColor(color: string): IssueTypePickerIssueType['color'] | null {
  switch (color.toUpperCase()) {
    case 'BLUE':
      return 'BLUE'
    case 'GRAY':
      return 'GRAY'
    case 'GREEN':
      return 'GREEN'
    case 'ORANGE':
      return 'ORANGE'
    case 'PINK':
      return 'PINK'
    case 'PURPLE':
      return 'PURPLE'
    case 'RED':
      return 'RED'
    case 'YELLOW':
      return 'YELLOW'
    default:
      return null
  }
}

export function issueTypeAppliedToAll(issues: ReadonlyArray<Pick<IssueItem, 'issueType'>>) {
  const issueType = issues[0]?.issueType
  if (!issueType?.id || !issues.every(issue => issue.issueType?.id === issueType.id)) return null

  const color = issueTypeColor(issueType.color)
  if (!color) return null

  return {
    id: issueType.id,
    name: issueType.name,
    description: null,
    color,
  } satisfies IssueTypePickerIssueType
}

export function RepoIssuesBulkIssueType({
  activeIssueType,
  disabled,
  nested,
  owner,
  repo,
  onUpdate,
}: RepoIssuesBulkActionProps & {activeIssueType: IssueTypePickerIssueType | null}) {
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => (
      <RepoIssuesBulkPickerAnchor {...{anchorProps, disabled, nested}} icon={IssueTracksIcon} label="Type" />
    ),
    [disabled, nested],
  )
  return (
    <IssueTypePicker
      anchorElement={anchorElement}
      title="Apply issue type"
      owner={owner}
      repo={repo}
      readonly={disabled}
      activeIssueType={activeIssueType}
      noTypeItem={NO_TYPE_ITEM}
      showUnset
      onSelectionChange={issueTypes => {
        const issueType = issueTypes?.[0]
        const clearIssueType = !issueType || issueType.id === SPECIAL_VALUES.noTypeData.id
        onUpdate(clearIssueType ? {clearIssueType: true} : {issueTypeId: issueType.id})
      }}
      nested={nested}
    />
  )
}
